import { HeaderIconType } from 'components/organisms/Header';
import { warn } from 'helper/debug';
import i18n from 'helper/i18n';
import appConfig from 'helper/mapBackendConfig';
import { template } from 'helper/string';
import { getIsAuthenticated } from 'store/login';
import { IRootState } from 'store/rootReducer';

type RouteMetaInfo = {
	description: string;
};

type RouteOpenGraphInfo = {
	title: string;
	image?: Object | string;
};

type SeoDataType = {
	title: string;
	meta: {
		description: string;
	};
	og: {
		title: string;
	};
};

const getRouteTitle = (subroute: string, useSpecTitle?: string): string => {
	return i18n(`react.impulsCoach.routes.${subroute}.${useSpecTitle || 'title'}`);
};

export const getRouteHref = (subroute: string): string => {
	return i18n(`react.impulsCoach.routes.${subroute}.href`);
};

const getRouteMeta = (subroute: string): RouteMetaInfo => {
	return {
		description: i18n(`react.impulsCoach.routes.${subroute}.meta.description`),
	};
};

const getRouteOG = (subroute: string): RouteOpenGraphInfo => {
	return {
		title: i18n(`react.impulsCoach.routes.${subroute}.og.title`),
	};
};

export type RouteType = {
	id: string;
	title: string;
	navigationTitle: string;
	href: string;
	icon: HeaderIconType;
	requireAuth?: boolean;
	requireAnamnese?: boolean;
	scrollTop?: boolean;
	config?: {
		title: string;
		subtitle: string;
		text: string;
	};
	og: {
		title: string;
	};
	meta: {
		description: string;
	};
	headerFooterInvisible?: boolean;
};

export type RoutesType = {
	[key: string]: {
		[key: string]: RouteType;
	};
};

export type StoreType = {
	getState: () => IRootState;
};

export type PreventedTransitionType = {
	from: Array<string>;
	to: Array<string>;
	middleware?: (store: StoreType, fromPathName: string, toPathName: string) => boolean | null;
};

export const preventedTransitions: {
	anamnesis: PreventedTransitionType;
	login: PreventedTransitionType;
} = {
	anamnesis: {
		from: [
			getRouteHref('anamnesis'),
			getRouteHref('anamnesis.start'),
			getRouteHref('anamnesis.selectCoach'),
			getRouteHref('anamnesis.selectGender'),
			getRouteHref('anamnesis.determineAge'),
			getRouteHref('anamnesis.determineHeight'),
			getRouteHref('anamnesis.determineWeight'),
			getRouteHref('anamnesis.defineHealthTarget'),
			getRouteHref('anamnesis.acceptHealthTarget'),
			getRouteHref('anamnesis.selectDietaryHabits'),
			getRouteHref('anamnesis.selectActivityLevel'),
			getRouteHref('anamnesis.verify'),
			getRouteHref('anamnesis.dsgvo'),
			getRouteHref('anamnesis.disclaimer'),
		],
		to: [
			getRouteHref('dashboard'),
			getRouteHref('program'),
			`${getRouteHref('program')}/${getRouteHref('program.recommended')}`,
			`${getRouteHref('program')}/${getRouteHref('program.further')}`,
			getRouteHref('today'),
			getRouteHref('today.day'),
			getRouteHref('profile'),
			getRouteHref('mySuccess'),
		],
		middleware(store: StoreType, fromPathName: string): boolean | null {
			if (!(fromPathName.search(getRouteHref('anamnesis.disclaimer')) >= 0)) {
				return null;
			}

			const state: IRootState = store.getState() || {};

			return !state.profile.anamnesisDisclaimerAccepted;
		},
	},
	login: {
		from: [getRouteHref('login')],
		to: [
			getRouteHref('dashboard'),
			getRouteHref('program'),
			getRouteHref('mySuccess'),
			getRouteHref('today'),
			getRouteHref('profile'),
		],
		middleware(store: StoreType, fromPathName: string): boolean | null {
			const cameFromLoginPage = !!~fromPathName.search(getRouteHref('login'));

			if (!cameFromLoginPage) {
				return null;
			}

			/*
			 * Do not prevent route transition if a user is logged in.
			 * This has the purpose to redirect from the login to e.g.dashboard
			 * for all authenticated users.
			 * @uses helper / middleware / history.js
			 */
			const state: IRootState = store.getState() || {};

			try {
				const isAuthenticated = getIsAuthenticated(state);
				return !isAuthenticated;
			} catch (e) {
				return false;
			}
		},
	},
};

const routes = {
	deepLink: {
		startProgram: {
			id: 'startProgram',
			title: getRouteTitle('deepLink.startProgram'),
			navigationTitle: getRouteTitle('deepLink.startProgram', 'navigationTitle'),
			href: getRouteHref('deepLink.startProgram'),
			og: getRouteOG('deepLink.startProgram'),
			meta: getRouteMeta('deepLink.startProgram'),
			icon: 'user' as 'user',
			requireAuth: true,
			requireAnamnese: false,
		},
	},
	pool: {
		indexRoute: {
			id: 'pool',
			title: getRouteTitle('pool'),
			navigationTitle: getRouteTitle('pool', 'navigationTitle'),
			href: getRouteHref('pool'),
			og: getRouteOG('pool'),
			meta: getRouteMeta('pool'),
			icon: 'user' as 'user',
			requireAuth: true,
			requireAnamnese: false,
			headerFooterInvisible: true,
		},
		detailRoute: {
			id: 'pool-card-detail',
			title: getRouteTitle('poolDetail'),
			navigationTitle: getRouteTitle('poolDetail', 'navigationTitle'),
			href: getRouteHref('poolDetail'),
			og: getRouteOG('poolDetail'),
			meta: getRouteMeta('poolDetail'),
			icon: 'user' as 'user',
			requireAuth: true,
			requireAnamnese: false,
			headerFooterInvisible: true,
		},
		customWorkoutRoute: {
			id: 'pool-card-custom-workout',
			title: getRouteTitle('poolCustomWorkout'),
			navigationTitle: getRouteTitle('poolCustomWorkout', 'navigationTitle'),
			href: getRouteHref('poolCustomWorkout'),
			og: getRouteOG('poolCustomWorkout'),
			meta: getRouteMeta('poolCustomWorkout'),
			icon: 'user' as 'user',
			requireAuth: true,
			requireAnamnese: false,
			headerFooterInvisible: true,
		},
	},
	dashboard: {
		indexRoute: {
			id: 'dashboard',
			title: getRouteTitle('dashboard'),
			navigationTitle: getRouteTitle('dashboard', 'navigationTitle'),
			href: getRouteHref('dashboard'),
			og: getRouteOG('dashboard'),
			meta: getRouteMeta('dashboard'),
			icon: 'overview' as 'overview',
			requireAuth: true,
			requireAnamnese: true,
		},
		newMeasurement: {
			id: 'dashboard-new-measurement',
			title: getRouteTitle('dashboard.newMeasurement'),
			navigationTitle: getRouteTitle('dashboard.newMeasurement', 'navigationTitle'),
			href: getRouteHref('dashboard.newMeasurement'),
			og: getRouteOG('dashboard.newMeasurement'),
			meta: getRouteMeta('dashboard.newMeasurement'),
			icon: 'overview' as 'overview',
			requireAuth: true,
			requireAnamnese: true,
			headerFooterInvisible: true,
		},
	},
	mySuccess: {
		indexRoute: {
			id: 'my-success',
			title: getRouteTitle('mySuccess'),
			navigationTitle: getRouteTitle('mySuccess', 'navigationTitle'),
			href: getRouteHref('mySuccess'),
			og: getRouteOG('mySuccess'),
			meta: getRouteMeta('mySuccess'),
			icon: 'badge' as 'badge',
			requireAuth: true,
			requireAnamnese: true,
		},
	},
	login: {
		indexRoute: {
			id: 'login',
			title: getRouteTitle('login'),
			navigationTitle: getRouteTitle('login', 'navigationTitle'),
			href: getRouteHref('login'),
			og: getRouteOG('login'),
			meta: getRouteMeta('login'),
			icon: 'user' as 'user',
			requireAuth: false,
			requireAnamnese: false,
		},
	},
	anamnesis: {
		indexRoute: {
			id: 'anamnesis',
			title: getRouteTitle('anamnesis'),
			navigationTitle: getRouteTitle('anamnesis', 'navigationTitle'),
			href: getRouteHref('anamnesis'),
			og: getRouteOG('anamnesis'),
			meta: getRouteMeta('anamnesis'),
			icon: 'user' as 'user',
			requireAuth: true,
			requireAnamnese: false,
		},
		start: {
			id: 'anamnesis-start',
			title: getRouteTitle('anamnesis.start'),
			navigationTitle: getRouteTitle('anamnesis.start', 'navigationTitle'),
			href: getRouteHref('anamnesis.start'),
			og: getRouteOG('anamnesis.start'),
			meta: getRouteMeta('anamnesis.start'),
			icon: 'user' as 'user',
			requireAuth: true,
			requireAnamnese: false,
		},
		selectCoach: {
			id: 'anamnesis-select-coach',
			title: getRouteTitle('anamnesis.selectCoach'),
			navigationTitle: getRouteTitle('anamnesis.selectCoach', 'navigationTitle'),
			href: getRouteHref('anamnesis.selectCoach'),
			og: getRouteOG('anamnesis.selectCoach'),
			meta: getRouteMeta('anamnesis.selectCoach'),
			icon: 'user' as 'user',
			requireAuth: true,
			requireAnamnese: false,
		},
		selectGender: {
			id: 'anamnesis-select-gender',
			title: getRouteTitle('anamnesis.selectGender'),
			navigationTitle: getRouteTitle('anamnesis.selectGender', 'navigationTitle'),
			href: getRouteHref('anamnesis.selectGender'),
			og: getRouteOG('anamnesis.selectGender'),
			meta: getRouteMeta('anamnesis.selectGender'),
			icon: 'user' as 'user',
			requireAuth: true,
			requireAnamnese: false,
		},
		determineAge: {
			id: 'anamnesis-determine-age',
			title: getRouteTitle('anamnesis.determineAge'),
			navigationTitle: getRouteTitle('anamnesis.determineAge', 'navigationTitle'),
			href: getRouteHref('anamnesis.determineAge'),
			og: getRouteOG('anamnesis.determineAge'),
			meta: getRouteMeta('anamnesis.determineAge'),
			icon: 'user' as 'user',
			requireAuth: true,
			requireAnamnese: false,
		},
		determineHeight: {
			id: 'anamnesis-determine-height',
			title: getRouteTitle('anamnesis.determineHeight'),
			navigationTitle: getRouteTitle('anamnesis.determineHeight', 'navigationTitle'),
			href: getRouteHref('anamnesis.determineHeight'),
			og: getRouteOG('anamnesis.determineHeight'),
			meta: getRouteMeta('anamnesis.determineHeight'),
			icon: 'user' as 'user',
			requireAuth: true,
			requireAnamnese: false,
		},
		determineWeight: {
			id: 'anamnesis-determine-weight',
			title: getRouteTitle('anamnesis.determineWeight'),
			navigationTitle: getRouteTitle('anamnesis.determineWeight', 'navigationTitle'),
			href: getRouteHref('anamnesis.determineWeight'),
			og: getRouteOG('anamnesis.determineWeight'),
			meta: getRouteMeta('anamnesis.determineWeight'),
			icon: 'user' as 'user',
			requireAuth: true,
			requireAnamnese: false,
		},
		defineHealthTarget: {
			id: 'anamnesis-define-health-target',
			title: getRouteTitle('anamnesis.defineHealthTarget'),
			navigationTitle: getRouteTitle('anamnesis.defineHealthTarget', 'navigationTitle'),
			href: getRouteHref('anamnesis.defineHealthTarget'),
			og: getRouteOG('anamnesis.defineHealthTarget'),
			meta: getRouteMeta('anamnesis.defineHealthTarget'),
			icon: 'user' as 'user',
			requireAuth: true,
			requireAnamnese: false,
		},
		acceptHealthTarget: {
			id: 'anamnesis-accept-health-target',
			title: getRouteTitle('anamnesis.acceptHealthTarget'),
			navigationTitle: getRouteTitle('anamnesis.acceptHealthTarget', 'navigationTitle'),
			href: getRouteHref('anamnesis.acceptHealthTarget'),
			og: getRouteOG('anamnesis.acceptHealthTarget'),
			meta: getRouteMeta('anamnesis.acceptHealthTarget'),
			icon: 'user' as 'user',
			requireAuth: true,
			requireAnamnese: false,
		},
		selectDietaryHabits: {
			id: 'anamnesis-select-dietary-habits',
			title: getRouteTitle('anamnesis.selectDietaryHabits'),
			navigationTitle: getRouteTitle('anamnesis.selectDietaryHabits', 'navigationTitle'),
			href: getRouteHref('anamnesis.selectDietaryHabits'),
			og: getRouteOG('anamnesis.selectDietaryHabits'),
			meta: getRouteMeta('anamnesis.selectDietaryHabits'),
			icon: 'user' as 'user',
			requireAuth: true,
			requireAnamnese: false,
		},
		selectActivityLevel: {
			id: 'anamnesis-select-activity-level',
			title: getRouteTitle('anamnesis.selectActivityLevel'),
			navigationTitle: getRouteTitle('anamnesis.selectActivityLevel', 'navigationTitle'),
			href: getRouteHref('anamnesis.selectActivityLevel'),
			og: getRouteOG('anamnesis.selectActivityLevel'),
			meta: getRouteMeta('anamnesis.selectActivityLevel'),
			icon: 'user' as 'user',
			requireAuth: true,
			requireAnamnese: false,
		},
		verify: {
			id: 'anamnesis-verify',
			title: getRouteTitle('anamnesis.verify'),
			navigationTitle: getRouteTitle('anamnesis.verify', 'navigationTitle'),
			href: getRouteHref('anamnesis.verify'),
			og: getRouteOG('anamnesis.verify'),
			meta: getRouteMeta('anamnesis.verify'),
			icon: 'user' as 'user',
			requireAuth: true,
			requireAnamnese: false,
		},
		dsgvo: {
			id: 'anamnesis-verify',
			title: getRouteTitle('anamnesis.dsgvo'),
			navigationTitle: getRouteTitle('anamnesis.dsgvo', 'navigationTitle'),
			href: getRouteHref('anamnesis.dsgvo'),
			og: getRouteOG('anamnesis.dsgvo'),
			meta: getRouteMeta('anamnesis.dsgvo'),
			icon: 'user' as 'user',
			requireAuth: true,
			requireAnamnese: false,
			scrollTop: true,
		},
		disclaimer: {
			id: 'anamnesis-disclaimer',
			title: getRouteTitle('anamnesis.disclaimer'),
			navigationTitle: getRouteTitle('anamnesis.disclaimer', 'navigationTitle'),
			href: getRouteHref('anamnesis.disclaimer'),
			og: getRouteOG('anamnesis.disclaimer'),
			meta: getRouteMeta('anamnesis.disclaimer'),
			icon: 'user' as 'user',
			requireAuth: true,
			requireAnamnese: false,
		},
	},
	profile: {
		indexRoute: {
			id: 'profile',
			title: getRouteTitle('profile'),
			navigationTitle: getRouteTitle('profile', 'navigationTitle'),
			href: getRouteHref('profile'),
			og: getRouteOG('profile'),
			meta: getRouteMeta('profile'),
			icon: 'user' as 'user',
			config: (appConfig.views.Profile as any).links,
			requireAuth: true,
			requireAnamnese: true,
		},
	},
	favorites: {
		indexRoute: {
			id: 'favorites',
			title: getRouteTitle('favorites'),
			navigationTitle: getRouteTitle('favorites', 'navigationTitle'),
			href: getRouteHref('favorites'),
			og: getRouteOG('favorites'),
			meta: getRouteMeta('favorites'),
			icon: 'heart' as 'heart',

			requireAuth: true,
			requireAnamnese: true,
		},
		cluster: {
			id: 'favorites-cluster',
			title: getRouteTitle('favorites.cluster'),
			navigationTitle: getRouteTitle('favorites.cluster', 'navigationTitle'),
			href: getRouteHref('favorites.cluster'),
			og: getRouteOG('favorites.cluster'),
			meta: getRouteMeta('favorites.cluster'),
			icon: 'user' as 'user',

			requireAuth: true,
			requireAnamnese: true,
			scrollTop: true,
		},
		detail: {
			id: 'favorites-detail',
			title: getRouteTitle('favorites.detail'),
			navigationTitle: getRouteTitle('favorites.detail', 'navigationTitle'),
			href: getRouteHref('favorites.detail'),
			og: getRouteOG('favorites.detail'),
			meta: getRouteMeta('favorites.detail'),
			icon: 'user' as 'user',

			requireAuth: true,
			requireAnamnese: true,
			headerFooterInvisible: true,
		},
	},
	program: {
		recommended: {
			id: 'program-recommended',
			title: getRouteTitle('program'),
			navigationTitle: getRouteTitle('program', 'navigationTitle'),
			href: `${getRouteHref('program')}/${getRouteHref('program.recommended')}`,
			og: getRouteOG('program'),
			meta: getRouteMeta('program'),
			icon: 'program' as 'program',
			requireAuth: true,
			requireAnamnese: true,
		},
		further: {
			id: 'program-further',
			title: getRouteTitle('program'),
			navigationTitle: getRouteTitle('program', 'navigationTitle'),
			href: `${getRouteHref('program')}/${getRouteHref('program.further')}`,
			og: getRouteOG('program'),
			meta: getRouteMeta('program'),
			icon: 'program' as 'program',
			requireAuth: true,
			requireAnamnese: true,
		},

		indexRoute: {
			id: 'program',
			title: getRouteTitle('program'),
			navigationTitle: getRouteTitle('program', 'navigationTitle'),
			href: getRouteHref('program'),
			og: getRouteOG('program'),
			meta: getRouteMeta('program'),
			icon: 'program' as 'program',
			requireAuth: true,
			requireAnamnese: true,
		},
		preview: {
			id: 'program-preview',
			title: getRouteTitle('program.preview'),
			navigationTitle: getRouteTitle('program.preview', 'navigationTitle'),
			href: getRouteHref('program.preview'),
			og: getRouteOG('program.preview'),
			meta: getRouteMeta('program.preview'),
			icon: 'program' as 'program',
			requireAuth: false,
			requireAnamnese: false,
		},
	},
	today: {
		indexRoute: {
			id: 'today',
			title: getRouteTitle('today'),
			navigationTitle: getRouteTitle('today', 'navigationTitle'),
			href: getRouteHref('today'),
			og: getRouteOG('today'),
			meta: getRouteMeta('today'),
			icon: 'calendar-day' as 'calendar-day',
			requireAuth: true,
			requireAnamnese: true,
		},
		day: {
			id: 'today-selected',
			title: getRouteTitle('today.day'),
			navigationTitle: getRouteTitle('today.day', 'navigationTitle'),
			href: getRouteHref('today.day'),
			og: getRouteOG('today.day'),
			meta: getRouteMeta('today.day'),
			icon: 'calendar-day' as 'calendar-day',
			requireAuth: true,
			requireAnamnese: true,
		},
	},
	// indexRoute: {
	// 	id: "rewards",
	// 	title: getRouteTitle("rewards"),
	// 	href: getRouteHref("rewards"),
	// 	icon: "user",
	// 	requireAuth: true,
	// 	requireAnamnese: true
	// }
	// },
	error40x: {
		indexRoute: {
			id: 'error-40x',
			title: getRouteTitle('error40x'),
			navigationTitle: getRouteTitle('error40x'),
			og: getRouteOG('error40x'),
			meta: getRouteMeta('error40x'),
			icon: 'user' as 'user',
			href: '',
			config: appConfig.views.Error40x as any,
			requireAuth: false,
			requireAnamnese: false,
		},
	},
};

export const getRouteById = (routeId: string): RouteType | undefined => {
	const matchedRoutes: any[] = [];

	Object.keys(routes).forEach((routeKey) => {
		const lvl1 = routes[routeKey];

		Object.keys(lvl1).forEach((lvl1Key) => {
			if (routes[routeKey][lvl1Key].id === routeId) {
				matchedRoutes.push(routes[routeKey][lvl1Key]);
			}
		});
	});

	if (matchedRoutes.length > 1) {
		warn(`Multiple routes with ID "${routeId}" found.`);
	}

	if (!matchedRoutes || matchedRoutes.length < 1) {
		return;
	}

	return matchedRoutes[0];
};

export const getSeoDataFromViewId = (viewId: string, compilableViewData?: Object): SeoDataType => {
	const { title = '', og = {} as any, meta = {} as any } = getRouteById(viewId) || {};
	const temporaryDataSet = {
		title,
		meta: {
			description: meta.description || '',
		},
		og: {
			title: og.title || '',
		},
	};

	if (compilableViewData) {
		return {
			...temporaryDataSet,
			title: template(temporaryDataSet.title, compilableViewData),
			meta: {
				description: template(temporaryDataSet.meta.description, compilableViewData),
			},
			og: {
				title: template(temporaryDataSet.og.title, compilableViewData),
			},
		};
	}

	return temporaryDataSet;
};

export const getParentsOfRoute = (routeId: string | null) => {
	if (routeId === null) {
		return [];
	}

	return Object.keys(routes).filter((routeKey) => {
		const lvl1 = routes[routeKey];
		const matchedChilds = Object.keys(lvl1).filter((childRouteKey) => {
			return routes[routeKey][childRouteKey].id === routeId;
		});
		return matchedChilds.length !== 0;
	});
};

export const getBaseTitle = (): string => {
	return `${i18n('react.impulsCoach.routes.title.separator')} ${i18n('react.impulsCoach.routes.title')}`;
};

export default routes;
